import {
	getMomentTz,
	MESApiService,
	type spParameter,
} from "@dexteel/mesf-core";
import { get } from "lodash-es";
import {
	UTILITY_CONSUMPTION_REPORT_MODE,
	UTILITY_CONSUMPTION_REPORT_ROW_KIND,
	type UtilityConsumptionAccountSplit,
	type UtilityConsumptionData,
	type UtilityConsumptionMainBucket,
	type UtilityConsumptionMeter,
	type UtilityConsumptionMeterGap,
	type UtilityConsumptionMeterStatus,
	type UtilityConsumptionMillRolling,
	type UtilityConsumptionMiniBucket,
	type UtilityConsumptionReportData,
	type UtilityConsumptionReportMeterRow,
	type UtilityConsumptionReportRequest,
	type UtilityConsumptionReportRow,
	type UtilityConsumptionReportSummary,
	type UtilityConsumptionRequest,
	type UtilityConsumptionSummary,
} from "../models/UtilityConsumption";

const moment = getMomentTz();

export const getUtilityConsumption = async (
	request: UtilityConsumptionRequest,
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "TargetId", value: request.targetId },
		{
			name: "ExternalMeterIdsJson",
			value:
				request.externalMeterIds === null
					? null
					: JSON.stringify(request.externalMeterIds),
		},
		{ name: "Mode", value: request.mode },
		{ name: "StartUtc", value: request.startUtc.toISOString() },
		{ name: "EndUtc", value: request.endUtc.toISOString() },
		{
			name: "WaitForEveryMeter",
			value: request.waitForEveryMeter ?? null,
		},
	];

	const resp = await apiService.callV2(
		"[SWV].[GetUtilityConsumption]",
		parameters,
		signal,
	);

	if (resp.ok) {
		const tables = get(resp, "data.tables", {});

		const s = get(tables, "1.rows.0", {}) as UtilityConsumptionSummary;
		const summary: UtilityConsumptionSummary = {
			...s,
			DataThroughUtc: s.DataThroughUtc
				? moment.utc(s.DataThroughUtc).toDate()
				: null,
			PreviousRollingStartUtc: s.PreviousRollingStartUtc
				? moment.utc(s.PreviousRollingStartUtc).toDate()
				: null,
			PreviousRollingEndUtc: s.PreviousRollingEndUtc
				? moment.utc(s.PreviousRollingEndUtc).toDate()
				: null,
		};

		const miniBuckets = (
			get(tables, "2.rows", []) as UtilityConsumptionMiniBucket[]
		).map((r) => ({
			...r,
			CurrentBucketStartUtc: r.CurrentBucketStartUtc
				? moment.utc(r.CurrentBucketStartUtc).toDate()
				: null,
			PreviousBucketStartUtc: moment.utc(r.PreviousBucketStartUtc).toDate(),
		}));

		const mainBuckets = (
			get(tables, "3.rows", []) as UtilityConsumptionMainBucket[]
		).map((r) => ({
			...r,
			BucketStartUtc: moment.utc(r.BucketStartUtc).toDate(),
		}));

		const millRolling = (
			get(tables, "4.rows", []) as UtilityConsumptionMillRolling[]
		).map((r) => ({
			...r,
			CurrentRollingStartUtc: r.CurrentRollingStartUtc
				? moment.utc(r.CurrentRollingStartUtc).toDate()
				: null,
		}));

		const accountSplits = (
			get(tables, "5.rows", []) as UtilityConsumptionAccountSplit[]
		).map((r) => ({ ...r, IsFromBill: !!r.IsFromBill }));

		const meterGaps = (
			get(tables, "6.rows", []) as UtilityConsumptionMeterGap[]
		).map((r) => ({
			...r,
			LatestReadTimeUtc: r.LatestReadTimeUtc
				? moment.utc(r.LatestReadTimeUtc).toDate()
				: null,
		}));

		const meterStatuses = (
			get(tables, "7.rows", []) as UtilityConsumptionMeterStatus[]
		).map((r) => ({
			...r,
			LatestReadTimeUtc: r.LatestReadTimeUtc
				? moment.utc(r.LatestReadTimeUtc).toDate()
				: null,
		}));

		return {
			summary,
			millRolling,
			miniBuckets,
			mainBuckets,
			accountSplits,
			meterGaps,
			meterStatuses,
		} as UtilityConsumptionData;
	} else {
		throw new Error(resp.message || "Error fetching utility consumption");
	}
};

export const getUtilityConsumptionMeters = async (
	targetId: number | null,
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2(
		"[SWV].[GetUtilityConsumptionMeters]",
		[{ name: "TargetId", value: targetId }],
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables.0.rows", []) as UtilityConsumptionMeter[];
	} else {
		throw new Error(resp.message || "Error fetching utility meters");
	}
};

export const getUtilityConsumptionReport = async (
	request: UtilityConsumptionReportRequest,
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "TargetId", value: request.targetId },
		{
			name: "ExternalMeterIdsJson",
			value:
				request.externalMeterIds === null
					? null
					: JSON.stringify(request.externalMeterIds),
		},
		{ name: "Mode", value: request.mode },
		...(request.mode === UTILITY_CONSUMPTION_REPORT_MODE.SECTION
			? [{ name: "SectionId", value: request.sectionId }]
			: []),
		{ name: "StartUtc", value: request.startUtc.toISOString() },
		{ name: "EndUtc", value: request.endUtc.toISOString() },
	];

	const resp = await apiService.callV2(
		"[SWV].[GetUtilityConsumptionReport]",
		parameters,
		signal,
	);

	if (resp.ok) {
		const rows = get(
			resp,
			"data.tables.0.rows",
			[],
		) as UtilityConsumptionReportMeterRow[];

		const summary = get(
			resp,
			"data.tables.1.rows.0",
			{},
		) as UtilityConsumptionReportSummary;

		const isSection = request.mode === UTILITY_CONSUMPTION_REPORT_MODE.SECTION;

		const meterIds: string[] = [];
		const seenMeterIds = new Set<string>();
		const meterTotalValue: Record<string, number | null> = {};
		const reportRows: UtilityConsumptionReportRow[] = [];
		const reportRowsByKey = new Map<string, UtilityConsumptionReportRow>();

		for (const row of rows) {
			if (!seenMeterIds.has(row.ExternalMeterId)) {
				seenMeterIds.add(row.ExternalMeterId);
				meterIds.push(row.ExternalMeterId);
			}
			meterTotalValue[row.ExternalMeterId] = row.MeterTotalGallons;

			const rowKey = isSection
				? `${row.MillAssetId}:${row.EventId}`
				: row.PeriodStartUtc;

			let reportRow = reportRowsByKey.get(rowKey);
			if (reportRow === undefined) {
				reportRow = isSection
					? {
							Kind: UTILITY_CONSUMPTION_REPORT_ROW_KIND.SECTION,
							MillAssetId: row.MillAssetId as number,
							MillName: row.MillName as string,
							EventId: row.EventId as number,
							SectionId: row.SectionId as number,
							SectionName: row.SectionName as string,
							EventStartUtc: moment.utc(row.EventStartUtc).toDate(),
							EventEndUtc: row.EventEndUtc
								? moment.utc(row.EventEndUtc).toDate()
								: null,
							PeriodStartUtc: moment.utc(row.PeriodStartUtc).toDate(),
							PeriodEndUtc: moment.utc(row.PeriodEndUtc).toDate(),
							MeterValue: {},
							MeterCoverage: {},
							MeterPartial: {},
							PeriodValue: row.PeriodGallons,
							ValuePerTon: row.GallonsPerTon,
							ValuePerBillet: row.GallonsPerBillet,
							PeriodCost: row.PeriodCost,
							CostPerTon: row.CostPerTon,
							IsCostApproximate: !!row.IsCostApproximate,
							IsValuePartial: false,
							IsValueApproximate: false,
							IsCostPartial: false,
						}
					: {
							Kind: UTILITY_CONSUMPTION_REPORT_ROW_KIND.CALENDAR,
							PeriodStartUtc: moment.utc(row.PeriodStartUtc).toDate(),
							MeterValue: {},
							MeterCoverage: {},
							MeterPartial: {},
							PeriodValue: row.PeriodGallons,
							ValuePerTon: row.GallonsPerTon,
							ValuePerBillet: row.GallonsPerBillet,
							PeriodCost: row.PeriodCost,
							CostPerTon: row.CostPerTon,
							IsCostApproximate: !!row.IsCostApproximate,
							IsValuePartial: false,
							IsValueApproximate: false,
							IsCostPartial: false,
						};

				reportRowsByKey.set(rowKey, reportRow);
				reportRows.push(reportRow);
			}

			reportRow.MeterValue[row.ExternalMeterId] = row.MeterGallons;
			reportRow.MeterCoverage[row.ExternalMeterId] =
				row.MeterReadings === null ||
				row.MeterExpectedReadings === null ||
				row.MeterExpectedReadings === 0
					? null
					: (row.MeterReadings / row.MeterExpectedReadings) * 100;
		}

		return {
			MeterIds: meterIds,
			Rows: reportRows,
			MeterTotalValue: meterTotalValue,
			MeterTotalPartial: {},
			GrandTotalValue: summary.GrandTotalGallons,
			GrandValuePerTon: summary.GrandGallonsPerTon,
			GrandValuePerBillet: summary.GrandGallonsPerBillet,
			GrandTotalCost: summary.GrandTotalCost,
			GrandCostPerTon: summary.GrandCostPerTon,
			IsCostApproximate: !!summary.IsCostApproximate,
			IsValuePartial: false,
			IsValueApproximate: false,
			IsCostPartial: false,
		} as UtilityConsumptionReportData;
	} else {
		throw new Error(
			resp.message || "Error fetching utility consumption report",
		);
	}
};
